import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { REGISTER_BANKS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/banks.ts';
import {
  ISSUER_NAV_LIST_QUERY_KEY,
  ISSUER_REGISTER_NAV_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  CreateAssetRequest,
  CreateBrokerRequest,
  CreatePortfolioCashMovementRequest,
  CreatePortfolioCashMovementResponse,
  CreatePortfolioNavsRequest,
  CreatePortfolioTransactionRequest,
  CreatePortfolioTransactionResponse,
  CurrencyEnum,
  GetAllAssetListResponse,
  GetAllAssetListV1SelfCustodyAllAssetsGetParams,
  GetAssetByIdResponse,
  GetAssetListResponse,
  GetAssetListV1SelfCustodyAssetGetParams,
  GetAssetsBreakdownChartResponse,
  GetAssetsBreakdownChartV1SelfCustodyChartsAssetsBreakdownGetParams,
  GetAssetsTreemapChartResponse,
  GetAssetsTreemapChartV1SelfCustodyChartsAssetsTreemapGetParams,
  GetBrokerByIdResponse,
  GetBrokerByIdV1SelfCustodyBrokersBrokerIdGetParams,
  GetBrokerListResponse,
  GetBrokerListV1SelfCustodyBrokersGetParams,
  GetCurrencyHistoricalFXRateResponse,
  GetCurrencyHistoricalFxV1SrCurrencyHistoricalFxGetParams,
  GetOverallBalanceStatisticV1SelfCustodyOverallBalanceStatisticGetParams,
  GetPnlOvertimeChartResponse,
  GetPnlOvertimeChartV1SelfCustodyChartsPnlOvertimeGetParams,
  GetPortfolioByIdResponse,
  GetPortfolioByIdV1SelfCustodyPortfolioPortfolioIdGetParams,
  GetPortfolioCashMovementListResponse,
  GetPortfolioCashMovementListV1SelfCustodyCashMovementGetParams,
  GetPortfolioListResponse,
  GetPortfolioListV1SelfCustodyPortfolioGetParams,
  GetPortfolioOverallBalanceResponse,
  GetPortfolioTransactionByIdResponse,
  GetPortfolioTransactionListResponse,
  GetPortfolioTransactionListV1SelfCustodyPortfolioTransactionGetParams,
  GetTotalCashInOutChartResponse,
  GetTotalCashInOutChartV1SelfCustodyChartsCashInOutGetParams,
  GetValueOvertimeChartResponse,
  GetValueOvertimeChartV1SelfCustodyChartsValueOvertimeGetParams,
  UpdateAssetRequest,
  UpdateBrokerRequest,
  UpdatePortfolioTransactionRequest,
  UpdatePortfolioTransactionResponse,
} from 'api';

export const ISSUER_PORTFOLIOS_LIST_QUERY_KEY = 'vestra/issuer-portfolios-list';
export const ISSUER_PORTFOLIO_BY_ID_QUERY_KEY = 'vestra/issuer-portfolio-by-id';
export const ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY = 'vestra/issuer-portfolio-assets-list';
// Keep this below the existing Assets prefix so all current asset/transaction
// invalidations also refresh the unified Assets tab.
export const ISSUER_ALL_ASSETS_LIST_QUERY_KEY = ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY;
export const ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY = 'vestra/issuer-portfolio-asset-by-id';
export const ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY = 'vestra/issuer-portfolio-brokers-list';
export const ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY = 'vestra/issuer-portfolio-broker-by-id';
export const ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY =
  'vestra/issuer-portfolio-transactions-list';
export const ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-transaction-by-id';

export const ISSUER_CURRENCY_HISTORICAL_FX_RATE_QUERY_KEY =
  'vestra/issuer-currency-historical-fx-rate';

export const ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY =
  'vestra/issuer-portfolio-cash-movements-list';
export const ISSUER_PORTFOLIO_CASH_MOVEMENT_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-cash-movement-by-id';
export const ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-treemap-chart-by-id';
export const ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-breakdown-chart-by-id';
export const ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-pnl-overtime-chart-by-id';
export const ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-total-cash_in_out-chart-by-id';
export const ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY =
  'vestra/issuer-portfolio-value-overtime-chart-by-id';
export const ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY = 'vestra/issuer-portfolio-overall-balance';
export const ISSUER_OUTDATED_ASSETS_QUERY_KEY = 'vestra/issuer-outdated-assets';

export interface OutdatedAsset {
  id: number;
  name: string;
  asset_type: string;
  price_per_share: string;
  currency: string;
  quantity: string;
  price_updated_at: string | null;
}

export interface GetOutdatedAssetsResponse {
  has_outdated: boolean;
  outdated_assets: OutdatedAsset[];
}

export const useIssuerOutdatedAssetsQuery = (disabled = false) => {
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  return useQuery<GetOutdatedAssetsResponse>({
    queryKey: [ISSUER_OUTDATED_ASSETS_QUERY_KEY, registerId],
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async () => {
      const response = await api.instance.get<GetOutdatedAssetsResponse>(
        '/v1/self-custody/assets/outdated',
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfoliosInfiniteListQuery = (
  payload: GetPortfolioListV1SelfCustodyPortfolioGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, ...data } = payload;
  return useInfiniteQuery<GetPortfolioListResponse>({
    queryKey: [ISSUER_PORTFOLIOS_LIST_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPortfolioListV1SelfCustodyPortfolioGet(
        {
          ...payload,
          page: pageParam || page,
        },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;

      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfoliosListQuery = (
  payload: GetPortfolioListV1SelfCustodyPortfolioGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetPortfolioListResponse>({
    queryKey: [ISSUER_PORTFOLIOS_LIST_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPortfolioListV1SelfCustodyPortfolioGet(payload, {
        headers: { 'xRegister-ID': payload.register_id || register_id },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioByIdQuery = (
  payload: GetPortfolioByIdV1SelfCustodyPortfolioPortfolioIdGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetPortfolioByIdResponse>({
    queryKey: [ISSUER_PORTFOLIO_BY_ID_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPortfolioByIdV1SelfCustodyPortfolioPortfolioIdGet(payload, {
        headers: { 'xRegister-ID': payload.register_id || register_id },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioAssetsListQuery = (
  payload: GetAssetListV1SelfCustodyAssetGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetAssetListResponse>({
    queryKey: [ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getAssetListV1SelfCustodyAssetGet(payload, {
        headers: { 'xRegister-ID': payload.register_id || register_id },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioAssetsInfiniteListQuery = (
  payload: GetAssetListV1SelfCustodyAssetGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, ...data } = payload;
  return useInfiniteQuery<GetAssetListResponse>({
    queryKey: [ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam }) => {
      const response = await api.v1.getAssetListV1SelfCustodyAssetGet(
        {
          ...payload,
          page: pageParam?.page || page,
          search: pageParam?.search || payload?.search,
        },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;

      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerAllAssetsInfiniteListQuery = (
  payload: GetAllAssetListV1SelfCustodyAllAssetsGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, ...data } = payload;
  return useInfiniteQuery<GetAllAssetListResponse>({
    queryKey: [ISSUER_ALL_ASSETS_LIST_QUERY_KEY, 'all', JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3,
    queryFn: async ({ pageParam }) => {
      const response = await api.v1.getAllAssetListV1SelfCustodyAllAssetsGet(
        { ...payload, page: pageParam || page },
        { headers: { 'xRegister-ID': payload.register_id || register_id } },
      );
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) =>
      Math.ceil(total / page_size) > page ? page + 1 : undefined,
    enabled: !disabled,
  });
};

export const useIssuerPortfolioAssetByIdQuery = (
  payload: { register_id?: number; assetId: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetAssetByIdResponse>({
    queryKey: [ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getAssetByIdV1SelfCustodyAssetAssetIdGet(
        { assetId: payload.assetId },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerAddPortfolioAssetMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreateAssetRequest) => {
      const response = await withErrorHandling(api.v1.createAssetV1SelfCustodyAssetPost(payload), {
        loading: 'Adding asset...',
        success: 'Asset created',
        setErrors,
      });

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerUpdatePortfolioAssetMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdateAssetRequest) => {
      const response = await withErrorHandling(api.v1.updateAssetV1SelfCustodyAssetPatch(payload), {
        loading: 'Updating asset...',
        success: 'Asset updated',
        setErrors,
      });

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_OUTDATED_ASSETS_QUERY_KEY]);

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerDeletePortfolioAssetMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (assetId: number) => {
      const response = await withErrorHandling(
        api.v1.deleteAssetByIdV1SelfCustodyAssetAssetIdDelete({ assetId }),
        {
          loading: 'Deleting asset...',
          success: 'Asset deleted',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      // await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerPortfolioTransactionsInfiniteListQuery = (
  payload: GetPortfolioTransactionListV1SelfCustodyPortfolioTransactionGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, ...data } = payload;
  return useInfiniteQuery<GetPortfolioTransactionListResponse>({
    queryKey: [ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPortfolioTransactionListV1SelfCustodyPortfolioTransactionGet(
        {
          ...payload,
          page: pageParam || page,
        },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;

      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioTransactionsListQuery = (
  payload: GetPortfolioTransactionListV1SelfCustodyPortfolioTransactionGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetPortfolioTransactionListResponse>({
    queryKey: [ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPortfolioTransactionListV1SelfCustodyPortfolioTransactionGet(
        payload,
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioTransactionByIdQuery = (
  payload: { register_id?: number; transactionId: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetPortfolioTransactionByIdResponse>({
    queryKey: [ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getPortfolioTransactionByIdV1SelfCustodyPortfolioTransactionTransactionIdGet(
          { transactionId: payload.transactionId },
          {
            headers: { 'xRegister-ID': payload.register_id || register_id },
          },
        );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerAddPortfolioTransactionMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: CreatePortfolioTransactionResponse) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreatePortfolioTransactionRequest) => {
      const response = await withErrorHandling(
        api.v1.createPortfolioTransactionV1SelfCustodyPortfolioTransactionPost(payload),
        {
          loading: 'Adding transaction...',
          success: 'Transaction created',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENT_BY_ID_QUERY_KEY],
      });

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);

      onSuccess && onSuccess(data);
    },
  });
};

export const useIssuerUpdatePortfolioTransactionMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: UpdatePortfolioTransactionResponse) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdatePortfolioTransactionRequest) => {
      const response = await withErrorHandling(
        api.v1.updatePortfolioTransactionV1SelfCustodyPortfolioTransactionPatch(payload),
        {
          loading: 'Updating transaction...',
          success: 'Transaction updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENT_BY_ID_QUERY_KEY],
      });

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);

      onSuccess && onSuccess(data);
    },
  });
};

export const useIssuerDeletePortfolioTransactionMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (transactionId: number) => {
      const response = await withErrorHandling(
        api.v1.deletePortfolioTransactionByIdV1SelfCustodyPortfolioTransactionTransactionIdDelete({
          transactionId,
        }),
        {
          loading: 'Deleting transaction...',
          success: 'Transaction deleted',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      // await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENT_BY_ID_QUERY_KEY],
      });

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);

      onSuccess && onSuccess();
    },
  });
};

export interface GetCurrencyHistoricalFxRateParams {
  from_currency?: CurrencyEnum;
  to_currency?: CurrencyEnum;
  fx_date?: string;
}

export const useIssuerCurrencyHistoricalFxRateQuery = (
  payload: GetCurrencyHistoricalFxRateParams,
  disabled = false,
) => {
  return useQuery<GetCurrencyHistoricalFXRateResponse>({
    queryKey: [ISSUER_CURRENCY_HISTORICAL_FX_RATE_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getCurrencyHistoricalFxV1SrCurrencyHistoricalFxGet(
        payload as GetCurrencyHistoricalFxV1SrCurrencyHistoricalFxGetParams,
      );
      return response.data;
    },
    enabled: !disabled && !!payload?.from_currency && !!payload?.to_currency && !!payload?.fx_date,
  });
};

export const useIssuerAddNewNavPortfolioMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreatePortfolioNavsRequest) => {
      const response = await withErrorHandling(
        api.v1.createPortfolioNavsV1SelfCustodyNavsPost(payload),
        {
          loading: 'Adding new NAV...',
          success: 'NAV added',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_NAV_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useIssuerPortfolioCashMovementInfiniteListQuery = (
  payload: GetPortfolioCashMovementListV1SelfCustodyCashMovementGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, ...data } = payload;
  return useInfiniteQuery<GetPortfolioCashMovementListResponse>({
    queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPortfolioCashMovementListV1SelfCustodyCashMovementGet(
        {
          ...payload,
          page: pageParam || page,
        },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;

      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerAddPortfolioCashMovementMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: CreatePortfolioCashMovementResponse) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreatePortfolioCashMovementRequest) => {
      const response = await withErrorHandling(
        api.v1.createPortfolioCashMovementV1SelfCustodyCashMovementPost(payload),
        {
          loading: 'Adding transaction...',
          success: 'Transaction created',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);

      onSuccess && onSuccess(data);
    },
  });
};

export const useIssuerPortfolioTreemapChartByIdQuery = (
  payload: GetAssetsTreemapChartV1SelfCustodyChartsAssetsTreemapGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetAssetsTreemapChartResponse>({
    queryKey: [
      ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY,
      JSON.stringify(payload),
      register_id,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getAssetsTreemapChartV1SelfCustodyChartsAssetsTreemapGet(
        payload,
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioBreakdownChartByIdQuery = (
  payload: GetAssetsBreakdownChartV1SelfCustodyChartsAssetsBreakdownGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetAssetsBreakdownChartResponse>({
    queryKey: [
      ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY,
      JSON.stringify(payload),
      register_id,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getAssetsBreakdownChartV1SelfCustodyChartsAssetsBreakdownGet(
        payload,
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioPnlOvertimeChartByIdQuery = (
  payload: GetPnlOvertimeChartV1SelfCustodyChartsPnlOvertimeGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetPnlOvertimeChartResponse>({
    queryKey: [
      ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY,
      JSON.stringify(payload),
      register_id,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPnlOvertimeChartV1SelfCustodyChartsPnlOvertimeGet(payload, {
        headers: { 'xRegister-ID': payload.register_id || register_id },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioTotalCashInOutChartByIdQuery = (
  payload: GetTotalCashInOutChartV1SelfCustodyChartsCashInOutGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetTotalCashInOutChartResponse>({
    queryKey: [
      ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY,
      JSON.stringify(payload),
      register_id,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTotalCashInOutChartV1SelfCustodyChartsCashInOutGet(payload, {
        headers: { 'xRegister-ID': payload.register_id || register_id },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioValueOvertimeChartByIdQuery = (
  payload: GetValueOvertimeChartV1SelfCustodyChartsValueOvertimeGetParams & {
    register_id?: number;
  },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetValueOvertimeChartResponse>({
    queryKey: [
      ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY,
      JSON.stringify(payload),
      register_id,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getValueOvertimeChartV1SelfCustodyChartsValueOvertimeGet(
        payload,
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioOverallBalanceStatisticQuery = (
  payload: GetOverallBalanceStatisticV1SelfCustodyOverallBalanceStatisticGetParams & {
    register_id?: number;
  } = {},
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetPortfolioOverallBalanceResponse>({
    queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getOverallBalanceStatisticV1SelfCustodyOverallBalanceStatisticGet(payload, {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioBrokersListQuery = (
  payload: GetBrokerListV1SelfCustodyBrokersGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetBrokerListResponse>({
    queryKey: [ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getBrokerListV1SelfCustodyBrokersGet(payload, {
        headers: { 'xRegister-ID': payload.register_id || register_id },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioBrokersInfiniteListQuery = (
  payload: GetBrokerListV1SelfCustodyBrokersGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, ...data } = payload;
  return useInfiniteQuery<GetBrokerListResponse>({
    queryKey: [ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getBrokerListV1SelfCustodyBrokersGet(
        {
          ...payload,
          page: pageParam || page,
        },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;

      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerPortfolioBrokerByIdQuery = (
  payload: GetBrokerByIdV1SelfCustodyBrokersBrokerIdGetParams & { register_id?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  return useQuery<GetBrokerByIdResponse>({
    queryKey: [ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getBrokerByIdV1SelfCustodyBrokersBrokerIdGet(
        { brokerId: payload.brokerId },
        {
          headers: { 'xRegister-ID': payload.register_id || register_id },
        },
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerAddPortfolioBrokerMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreateBrokerRequest) => {
      const response = await withErrorHandling(
        api.v1.createBrokerV1SelfCustodyBrokersPost(payload),
        {
          loading: 'Adding broker...',
          success: 'Broker created',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerUpdatePortfolioBrokerMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdateBrokerRequest) => {
      const response = await withErrorHandling(
        api.v1.updateBrokerV1SelfCustodyBrokersPatch(payload),
        {
          loading: 'Updating broker...',
          success: 'Broker updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerDeletePortfolioBrokerMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (brokerId: number) => {
      const response = await withErrorHandling(
        api.v1.deleteBrokerByIdV1SelfCustodyBrokersBrokerIdDelete({ brokerId }),
        {
          loading: 'Deleting broker...',
          success: 'Broker deleted',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKERS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BROKER_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIOS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      // await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);

      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerRefreshAssetPriceMutation = () => {
  return useMutation({
    mutationFn: async (assetId: number) => {
      const response = await withErrorHandling(
        api.v1.refreshAssetPriceV1SelfCustodyAssetAssetIdRefreshPricePost({ assetId }),
        {
          loading: 'Updating price...',
          success: 'Price updated',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_OUTDATED_ASSETS_QUERY_KEY]);
    },
  });
};
